export type RuntimeEvidenceProgramFamily = 'SAH' | 'RAC' | 'OTHER' | 'SHARED';
export const PROVIDER_RUNTIME_EVIDENCE_REVISION = 'experience-cohorts-2026-08-28.12';

export interface RuntimeResponseEvidence {
  programFamily: RuntimeEvidenceProgramFamily;
  programContextRevision: string;
  evidenceRevision: string;
  entityIds?: readonly string[];
  relationshipIds?: readonly string[];
  outcomeTokens?: readonly string[];
}

const SAFE_VALUE = /^[^\r\n]{1,512}$/u;

export function providerRuntimeEvidenceEnabledForSupabaseUrl(value: string | undefined): boolean {
  try {
    const url = new URL(value ?? '');
    return (
      url.protocol === 'http:' &&
      ['localhost', '127.0.0.1', '::1'].includes(url.hostname) &&
      url.port === '55321'
    );
  } catch {
    return false;
  }
}

function exactValues(values: readonly string[] | undefined): string[] {
  return [...new Set(values ?? [])]
    .map((value) => value.trim())
    .filter(Boolean)
    .sort()
    .slice(0, 32);
}

function encodedList(values: readonly string[] | undefined): string {
  return exactValues(values).map(encodeURIComponent).join('|');
}

/**
 * Shared response-header producer for routes that return exact provider-runtime
 * rows. Callers must derive these values from the same trusted server query and
 * programme context as the response body; request/query hints are not valid.
 */
export function providerRuntimeResponseEvidenceHeaders(
  evidence: RuntimeResponseEvidence,
): Record<string, string> {
  for (const [name, value] of Object.entries({
    programContextRevision: evidence.programContextRevision,
    evidenceRevision: evidence.evidenceRevision,
  })) {
    if (!SAFE_VALUE.test(value)) throw new Error(`provider runtime ${name} is invalid`);
  }
  const headers: Record<string, string> = {
    'x-statura-program-family': evidence.programFamily,
    'x-statura-program-context-revision': evidence.programContextRevision,
    'x-statura-provider-evidence-revision': evidence.evidenceRevision,
  };
  for (const [name, values] of [
    ['x-statura-provider-entity-ids', evidence.entityIds],
    ['x-statura-provider-relationship-ids', evidence.relationshipIds],
    ['x-statura-provider-outcome-tokens', evidence.outcomeTokens],
  ] as const) {
    const value = encodedList(values);
    if (value) headers[name] = value;
  }
  return headers;
}

export function applyProviderRuntimeResponseEvidence(
  headers: Headers,
  evidence: RuntimeResponseEvidence,
): Headers {
  for (const [name, value] of Object.entries(providerRuntimeResponseEvidenceHeaders(evidence))) {
    headers.set(name, value);
  }
  return headers;
}
